import type { CSSProperties } from "react"
import { useLingui } from "@lingui/react/macro"
import {
  ActionPill,
  PillCheckGlyph,
  PillXGlyph,
} from "@/components/v2/action-pill"

// ── SuggestionCard (CON-215 Option B / CON-223) ────────────
// The ONE AI-suggestion card, shared by every card-grammar
// surface that proposes an AI-generated next step (inbox
// context pane + mobile more-menu, post-call follow-ups,
// deal drawer). Layout: full-width title, ONE sparkle meta
// line whose brand crumb carries provenance ("from your
// call Monday" / "AI suggested"), then an Accept pill +
// ghost Dismiss (+ optional ghost Edit) in their own
// actions row — all `ActionPill`s, the same family the
// Today row templates use.
//
// Today's list rows do NOT use this card — they render the
// unified Today row template (activity-row /
// mobile-activity-row) where the suggestion is just a skin
// on the activity skeleton. Same pills, same skin language,
// different grammar.
//
// Palette is parameterized so the dark post-call card and
// the light panes share markup. Key names intentionally
// match the subset of `FollowUpTheme`
// (`@/components/v2/voice/call-follow-ups`) so a
// `FollowUpTheme` is structurally assignable here.

export interface SuggestionPalette {
  suggestedBg: string
  suggestedBorder: string
  bodyText: string
  brandText: string
  mutedText: string
  acceptBg: string
  acceptText: string
  dismissText: string
  dismissHoverBg: string
  // Hairline border on ghost pills — keeps the ghost
  // readable on white cards and identical across the
  // pill family (CON-223 one-template review).
  ghostBorder: string
}

export const SUGGESTION_LIGHT: SuggestionPalette = {
  suggestedBg: "#FBF6EE",
  suggestedBorder: "#E5C896",
  bodyText: "#1E1B17",
  brandText: "#B86A2D",
  mutedText: "#9A8F7E",
  acceptBg: "#B86A2D",
  acceptText: "#FFFFFF",
  dismissText: "#6B6357",
  dismissHoverBg: "#F0EAD9",
  ghostBorder: "#DDD6C7",
}

export const SUGGESTION_DARK: SuggestionPalette = {
  suggestedBg: "#252019",
  suggestedBorder: "#3A352E",
  bodyText: "#FBF7F0",
  brandText: "#E0A56A",
  mutedText: "#9A938A",
  acceptBg: "#E0A56A",
  acceptText: "#1E1B17",
  dismissText: "#C3BAB0",
  dismissHoverBg: "#2F2A24",
  ghostBorder: "#3A352E",
}

// NBSP-padded middle dot so the separator never word-wraps
// apart from its neighbours (CON-215 — matches the meta-line
// separator used by every suggestion-row callsite).
export const SUGGESTION_META_SEP = " · "

export interface SuggestionCrumb {
  label: string
  // When present, the crumb is itself the click-to-open
  // affordance (e.g. open the source call). Without it the
  // crumb is plain provenance text ("AI suggested").
  onClick?: () => void
  testId?: string
}

export interface SuggestionCardTestIds {
  root?: string
  accept?: string
  dismiss?: string
  edit?: string
  error?: string
}

export interface SuggestionCardProps {
  title: string
  // Brand provenance crumb: "from your call Monday" |
  // "AI suggested".
  crumb: SuggestionCrumb
  // Muted facts rendered after the crumb, " · "-joined;
  // empties are dropped.
  metaFacts?: Array<string | null | undefined>
  palette?: SuggestionPalette
  onAccept: () => void
  onDismiss: () => void
  // When present, a ghost "Edit" pill renders between Accept
  // and Dismiss (label-only, no icon).
  onEdit?: () => void
  // Disables all buttons; the Accept label becomes busyLabel.
  busy?: boolean
  busyLabel?: string
  error?: string | null
  testIds?: SuggestionCardTestIds
}

export function SuggestionCard({
  title,
  crumb,
  metaFacts,
  palette = SUGGESTION_LIGHT,
  onAccept,
  onDismiss,
  onEdit,
  busy = false,
  busyLabel,
  error,
  testIds,
}: SuggestionCardProps) {
  const { t } = useLingui()
  const resolvedBusyLabel = busyLabel ?? t`Adding…`
  const facts = (metaFacts ?? []).filter(
    (fact): fact is string => Boolean(fact),
  )

  const rootStyle: CSSProperties = {
    backgroundColor: palette.suggestedBg,
    borderColor: palette.suggestedBorder,
  }

  return (
    <div
      data-testid={testIds?.root}
      className="flex flex-col gap-1.5 rounded-[11px] py-2.5 px-3 border border-dashed"
      style={rootStyle}
    >
      <span
        className="text-[13px] leading-[18px] break-words"
        style={{ color: palette.bodyText }}
      >
        {title}
      </span>

      {/* The crumb never breaks mid-phrase
          (`whitespace-nowrap`); when the line runs out of
          room the muted facts span wraps below instead
          ("AI sugg-\nested" reads broken, a wrapped facts
          line doesn't). */}
      <div className="flex flex-wrap items-center gap-1 text-[11px] leading-3.5 min-w-0">
        <SparkleGlyph color={palette.brandText} />
        {crumb.onClick ? (
          <button
            type="button"
            data-testid={crumb.testId}
            onClick={crumb.onClick}
            disabled={busy}
            className="whitespace-nowrap hover:underline disabled:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded cursor-pointer disabled:cursor-default"
            style={{ color: palette.brandText, fontWeight: 500 }}
          >
            {crumb.label}
          </button>
        ) : (
          <span
            data-testid={crumb.testId}
            className="whitespace-nowrap"
            style={{ color: palette.brandText, fontWeight: 500 }}
          >
            {crumb.label}
          </span>
        )}
        {facts.length > 0 ? (
          <span className="min-w-0" style={{ color: palette.mutedText }}>
            {`${SUGGESTION_META_SEP}${facts.join(SUGGESTION_META_SEP)}`}
          </span>
        ) : null}
      </div>

      <div className="flex items-center gap-2 pt-0.5">
        <ActionPill
          variant="solid"
          label={busy ? resolvedBusyLabel : t`Accept`}
          icon={<PillCheckGlyph color={palette.acceptText} />}
          bg={palette.acceptBg}
          fg={palette.acceptText}
          onClick={onAccept}
          disabled={busy}
          testId={testIds?.accept}
        />
        {onEdit ? (
          <ActionPill
            variant="ghost"
            label={t`Edit`}
            fg={palette.dismissText}
            border={palette.ghostBorder}
            hoverBg={palette.dismissHoverBg}
            onClick={onEdit}
            disabled={busy}
            testId={testIds?.edit}
          />
        ) : null}
        <ActionPill
          variant="ghost"
          label={t`Dismiss`}
          icon={<PillXGlyph color={palette.dismissText} />}
          fg={palette.dismissText}
          border={palette.ghostBorder}
          hoverBg={palette.dismissHoverBg}
          onClick={onDismiss}
          disabled={busy}
          testId={testIds?.dismiss}
          ariaLabel={t`Dismiss suggestion`}
        />
      </div>

      {error ? (
        <p
          data-testid={testIds?.error}
          className="text-[13px]"
          style={{ color: "#B3402E" }}
        >
          {error}
        </p>
      ) : null}
    </div>
  )
}

// ── Glyphs ─────────────────────────────────────────────────
// 4-point sparkle — the AI-authorship marker. 10px in meta
// lines; the Today templates render it at 13px in the lead
// slot. Exported for both.
export function SparkleGlyph({
  color,
  size = 10,
}: {
  color: string
  size?: number
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className="shrink-0"
    >
      <path
        d="M8 1C8 4 9 7 12 7C15 7 8 8 8 8C8 8 15 9 12 9C9 9 8 12 8 15C8 12 7 9 4 9C1 9 8 8 8 8C8 8 1 7 4 7C7 7 8 4 8 1Z"
        fill={color}
      />
    </svg>
  )
}
